import React from 'react';
import { Menu } from 'antd';
import {
  DashboardOutlined,
  ShoppingOutlined,
  TagsOutlined,
  OrderedListOutlined,
  BarChartOutlined,
  UserOutlined,
  SettingOutlined,
  PictureOutlined,
  ShopOutlined,
  FileTextOutlined,
  TeamOutlined,
  ToolOutlined,
  DollarOutlined,
  CrownOutlined,
  ShareAltOutlined,
  UsergroupAddOutlined,
  GiftOutlined,
  RollbackOutlined,
  BellOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import AuthService from '../../utils/auth';

const menuKeyToPath = {
  dashboard: '/',
  banner: '/banner',
  'home-sections': '/home-sections',
  'product-list': '/product',
  'product-add': '/product/add',
  'stock-alert': '/stock-alert',
  category: '/category',
  subcategory: '/subcategory',
  'discount-list': '/discount',
  order: '/order',
  'return-request': '/return-request',
  'audit-log': '/audit-log',
  stats: '/stats',
  'refund-stats': '/refund-stats',
  'sales-report': '/sales-report',
  user: '/user',
  'user-recharge': '/user/recharge',
  'user-member': '/user/member',
  // 分销路径映射全部启用
  'distribution-list': '/distribution',
  'distribution-stats': '/distribution-stats',
  'distribution-commission': '/distribution-commission',
  'distribution-config': '/distribution-config',
  'settings-basic': '/settings',
  'settings-payment': '/settings',
  'settings-shipping': '/settings',
  'shipping-rule': '/shipping-rule',
  'home-sections': '/home-sections',
  'settings-sms': '/settings',
  'settings-email': '/settings',
};

const pathToMenuKey = Object.entries(menuKeyToPath).reduce((acc, [k, v]) => {
  acc[v] = k;
  return acc;
}, {});

const menuPermissions = {
  settings: ['settings:view', 'system:settings:view', 'settings:manage'],
};

const filterMenuByPermission = items => items
  .filter(item => {
    const required = menuPermissions[item.key];
    return !required || AuthService.hasAnyPermission(required);
  })
  .map(item => item.children
    ? { ...item, children: filterMenuByPermission(item.children) }
    : item)
  .filter(item => !item.children || item.children.length > 0);

const SidebarMenu = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // 处理高亮
  const selectedKey = pathToMenuKey[location.pathname] || 'dashboard';
  // 处理展开（如settings、product、user）
  let openKeys = [];
  if (selectedKey.startsWith('settings')) openKeys.push('settings');
  if (selectedKey.startsWith('product')) openKeys.push('product');
  if (selectedKey.startsWith('user')) openKeys.push('user-management');
  // 逐步启用分销菜单展开逻辑
  if (selectedKey.startsWith('distribution')) openKeys.push('distribution-management');

  // 现代化品牌Logo区域 - 深色专业风格
  const BrandLogo = () => (
    <div className="sidebar-brand">
      <div className="sidebar-brand-mark">洁</div>
      <div className="sidebar-brand-copy">
        <div className="sidebar-brand-name">心洁茗茶</div>
        <div className="sidebar-brand-subtitle">运营管理</div>
      </div>
    </div>
  );

  // Modern menu group title component
  const MenuGroupTitle = ({ title }) => (
    <div className="sidebar-group-title">{title}</div>
  );

  const menuItems = [
    {
      key: 'dashboard',
      icon: <DashboardOutlined style={{ fontSize: '16px' }} />,
      label: (
        <span style={{ fontSize: '14px', fontWeight: '500' }}>
          系统概览
        </span>
      ),
    },
    {
      type: 'divider',
      style: {
        margin: '8px 16px',
        borderColor: 'rgba(255,255,255,0.1)'
      }
    },
    {
      key: 'content-group',
      label: <MenuGroupTitle title="内容与商品" />,
      type: 'group',
      children: [
        {
          key: 'banner',
          icon: <PictureOutlined style={{ fontSize: '16px' }} />,
          label: (
            <span style={{ fontSize: '14px', fontWeight: '500' }}>
              轮播图管理
            </span>
          ),
        },
        {
          key: 'product',
          icon: <ShopOutlined style={{ fontSize: '16px' }} />,
          label: (
            <span style={{ fontSize: '14px', fontWeight: '500' }}>
              商品管理
            </span>
          ),
          children: [
            {
              key: 'product-list',
              icon: <ShoppingOutlined style={{ fontSize: '14px' }} />,
              label: (
                <span style={{ fontSize: '13px' }}>
                  商品列表
                </span>
              ),
            },
            {
              key: 'stock-alert',
              icon: <BellOutlined style={{ fontSize: '14px' }} />,
              label: (
                <span style={{ fontSize: '13px' }}>
                  低库存提醒
                </span>
              ),
            },
            {
              key: 'category',
              icon: <TagsOutlined style={{ fontSize: '14px' }} />,
              label: (
                <span style={{ fontSize: '13px' }}>
                  分类管理
                </span>
              ),
            },
            {
              key: 'subcategory',
              icon: <TagsOutlined style={{ fontSize: '14px' }} />,
              label: (
                <span style={{ fontSize: '13px' }}>
                  二级分类管理
                </span>
              ),
            },
            {
              key: 'discount-list',
              icon: <TagsOutlined style={{ fontSize: '14px' }} />,
              label: (
                <span style={{ fontSize: '13px' }}>
                  折扣管理
                </span>
              ),
            },
          ],
        },
        {
          key: 'home-sections',
          icon: <PictureOutlined style={{ fontSize: '16px' }} />,
          label: (
            <span style={{ fontSize: '14px', fontWeight: '500' }}>
              首页板块
            </span>
          ),
        },
      ]
    },
    {
      key: 'business-group',
      label: <MenuGroupTitle title="交易与客户" />,
      type: 'group',
      children: [
        {
          key: 'order',
          icon: <FileTextOutlined style={{ fontSize: '16px' }} />,
          label: (
            <span style={{ fontSize: '14px', fontWeight: '500' }}>
              订单管理
            </span>
          ),
        },
        {
          key: 'return-request',
          icon: <RollbackOutlined style={{ fontSize: '16px' }} />,
          label: (
            <span style={{ fontSize: '14px', fontWeight: '500' }}>
              退货管理
            </span>
          ),
        },
        {
          key: 'user-management',
          icon: <TeamOutlined style={{ fontSize: '16px' }} />,
          label: (
            <span style={{ fontSize: '14px', fontWeight: '500' }}>
              用户管理
            </span>
          ),
          children: [
            {
              key: 'user',
              icon: <UserOutlined style={{ fontSize: '14px' }} />,
              label: (
                <span style={{ fontSize: '13px' }}>
                  用户列表
                </span>
              ),
            },
            {
              key: 'user-recharge',
              icon: <DollarOutlined style={{ fontSize: '14px' }} />,
              label: (
                <span style={{ fontSize: '13px' }}>
                  充值管理
                </span>
              ),
            },
            {
              key: 'user-member',
              icon: <CrownOutlined style={{ fontSize: '14px' }} />,
              label: (
                <span style={{ fontSize: '13px' }}>
                  会员管理
                </span>
              ),
            },
          ],
        },
        {
          key: 'distribution-management',
          icon: <ShareAltOutlined style={{ fontSize: '16px' }} />,
          label: (
            <span style={{ fontSize: '14px', fontWeight: '500' }}>
              分销管理
            </span>
          ),
          children: [
            {
              key: 'distribution-list',
              icon: <UsergroupAddOutlined style={{ fontSize: '14px' }} />,
              label: (
                <span style={{ fontSize: '13px' }}>
                  分销商管理
                </span>
              ),
            },
            {
              key: 'distribution-commission',
              icon: <DollarOutlined style={{ fontSize: '14px' }} />,
              label: (
                <span style={{ fontSize: '13px' }}>
                  佣金管理
                </span>
              ),
            },
            {
              key: 'distribution-stats',
              icon: <BarChartOutlined style={{ fontSize: '14px' }} />,
              label: (
                <span style={{ fontSize: '13px' }}>
                  分销统计
                </span>
              ),
            },
            {
              key: 'distribution-config',
              icon: <SettingOutlined style={{ fontSize: '14px' }} />,
              label: (
                <span style={{ fontSize: '13px' }}>
                  分销配置
                </span>
              ),
            },
            // {
            //   key: 'distribution-config',
            //   icon: <SettingOutlined style={{ fontSize: '14px' }} />,
            //   label: (
            //     <span style={{ fontSize: '13px' }}>
            //       分销配置
            //     </span>
            //   ),
            // },
          ],
        },
        {
          key: 'audit-log',
          icon: <ClockCircleOutlined style={{ fontSize: '16px' }} />,
          label: (
            <span style={{ fontSize: '14px', fontWeight: '500' }}>
              操作审计
            </span>
          ),
        },
        {
          key: 'stats',
          icon: <BarChartOutlined style={{ fontSize: '16px' }} />,
          label: (
            <span style={{ fontSize: '14px', fontWeight: '500' }}>
              数据统计
            </span>
          ),
        },
        {
          key: 'refund-stats',
          icon: <RollbackOutlined style={{ fontSize: '16px' }} />,
          label: (
            <span style={{ fontSize: '14px', fontWeight: '500' }}>
              退款统计
            </span>
          ),
        },
        {
          key: 'sales-report',
          icon: <BarChartOutlined style={{ fontSize: '16px' }} />,
          label: (
            <span style={{ fontSize: '14px', fontWeight: '500' }}>
              销售报表
            </span>
          ),
        },
      ]
    },
    {
      key: 'system-group',
      label: <MenuGroupTitle title="系统与配置" />,
      type: 'group',
      children: [
        {
          key: 'settings',
          icon: <ToolOutlined style={{ fontSize: '16px' }} />,
          label: (
            <span style={{ fontSize: '14px', fontWeight: '500' }}>
              系统配置
            </span>
          ),
          children: [
            {
              key: 'settings-basic',
              icon: <SettingOutlined style={{ fontSize: '14px' }} />,
              label: (
                <span style={{ fontSize: '13px' }}>
                  基本设置
                </span>
              ),
            },
            {
              key: 'settings-payment',
              icon: <OrderedListOutlined style={{ fontSize: '14px' }} />,
              label: (
                <span style={{ fontSize: '13px' }}>
                  支付设置
                </span>
              ),
            },
            {
              key: 'settings-shipping',
              icon: <OrderedListOutlined style={{ fontSize: '14px' }} />,
              label: (
                <span style={{ fontSize: '13px' }}>
                  物流设置
                </span>
              ),
            },
            {
              key: 'settings-sms',
              icon: <OrderedListOutlined style={{ fontSize: '14px' }} />,
              label: (
                <span style={{ fontSize: '13px' }}>
                  短信设置
                </span>
              ),
            },
            {
              key: 'settings-email',
              icon: <OrderedListOutlined style={{ fontSize: '14px' }} />,
              label: (
                <span style={{ fontSize: '13px' }}>
                  邮件设置
                </span>
              ),
            },
          ],
        },
      ]
    }
  ];
  const visibleMenuItems = filterMenuByPermission(menuItems);

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <BrandLogo />

      <div style={{ flex: 1, overflow: 'auto' }}>
        <Menu
          mode='inline'
          style={{
            height: '100%',
            borderRight: 0,
            background: '#102b24',
            fontSize: '14px',
            color: '#ffffff',
            padding: '10px 8px'
          }}
          theme="dark"
          selectedKeys={[selectedKey]}
          defaultOpenKeys={['product', 'settings', 'user-management', 'distribution-management']}
          items={visibleMenuItems}
          onClick={({ key }) => {
            const path = menuKeyToPath[key];
            if (path) navigate(path);
          }}
        />
      </div>

    </div>
  );
};

export default SidebarMenu;
